import { useMemo, useRef, useState } from 'react';
import { Box } from '@mui/material';
import type { UsageTrendPoint } from '../../api/usage';
import styles from '../../css/usageDashboard.module.css';

/**
 * 세로축 강조 강도(0~1). 1에 가까울수록 최소값에 바짝 붙어 변화가 커 보인다.
 *   여백 = (최대 - 최소) x (1 - AMPLIFY)
 * 0으로 클램프되므로 값이 0에 가까운 구간에서는 절단이 일어나지 않는다.
 * 막대(MAU)와 선(질의 건수)에 같은 규칙을 적용한다.
 */
const AMPLIFY = 0.7;
const HEADROOM = 0.18;

const W = 920;
const H = 300;
const PAD_L = 46;
const PAD_R = 58;
const PAD_T = 16;
const PAD_B = 40;
const PLOT_W = W - PAD_L - PAD_R;
const PLOT_H = H - PAD_T - PAD_B;
const BASE_Y = PAD_T + PLOT_H;

type Domain = { lo: number; hi: number; cut: boolean };

function niceMax(v: number): number {
  if (v <= 0) return 10;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  return (Math.ceil((v / mag) * 2) / 2) * mag;
}

function domain(values: number[]): Domain {
  if (!values.length) return { lo: 0, hi: 10, cut: false };

  const max = Math.max(...values);
  const min = Math.min(...values);

  if (min === max || max <= 0) return { lo: 0, hi: niceMax(max), cut: false };

  const span = max - min;
  const lo = Math.max(0, min - span * (1 - AMPLIFY));
  return { lo, hi: max + span * HEADROOM, cut: lo > 0 };
}

function comma(n: number): string {
  return n.toLocaleString('ko-KR');
}

function monthLabel(month: string, index: number): string {
  const [year, mm] = month.split('-');
  const m = parseInt(mm, 10);
  return m === 1 || index === 0 ? `${year}.${m}` : `${m}월`;
}

function delta(cur: number, prev: number | undefined): number | null {
  if (prev === undefined || prev === 0) return null;
  return Math.round(((cur - prev) / prev) * 1000) / 10;
}

type Props = {
  points: UsageTrendPoint[];
  /** yyyy-MM. 아래 표에서 보고 있는 달 */
  selectedMonth: string;
  onSelectMonth: (month: string) => void;
};

export default function UsageTrendChart({ points, selectedMonth, onSelectMonth }: Props) {
  const [hovered, setHovered] = useState<number | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  const live = useMemo(() => points.filter((p) => p.queries > 0 || p.mau > 0), [points]);
  const mauDom = useMemo(() => domain(live.map((p) => p.mau)), [live]);
  const qDom = useMemo(() => domain(live.map((p) => p.queries)), [live]);

  if (!points.length) return null;

  const band = PLOT_W / points.length;
  const barW = Math.min(34, band * 0.46);
  const isCut = mauDom.cut || qDom.cut;

  const yIn = (v: number, dom: Domain) => {
    const t = (v - dom.lo) / (dom.hi - dom.lo);
    return PAD_T + PLOT_H * (1 - Math.max(0, Math.min(1, t)));
  };
  const cx = (i: number) => PAD_L + band * i + band / 2;
  const has = (p: UsageTrendPoint) => p.queries > 0 || p.mau > 0;

  const ticks = [0, 1, 2, 3, 4];

  const linePoints = points
    .map((p, i) => (has(p) ? `${cx(i)},${yIn(p.queries, qDom)}` : null))
    .filter(Boolean)
    .join(' ');

  let zig = `M${PAD_L} ${BASE_Y}`;
  for (let x = PAD_L; x < PAD_L + PLOT_W; x += 12) {
    zig += ` L${x + 6} ${BASE_Y - 4} L${x + 12} ${BASE_Y}`;
  }

  const tipIndex = hovered;
  const tipPoint = tipIndex === null ? null : points[tipIndex];

  return (
    <Box className={styles.chart} ref={wrapRef}>
      <div className={styles.legend}>
        <span className={styles.key}><span className={styles.keyBar} />MAU (월 순이용자, 막대)</span>
        <span className={styles.key}><span className={styles.keyLine} />질의 건수 (선)</span>
        <span className={styles.key}><span className={styles.keySel} />아래 표에서 보고 있는 달</span>
        {isCut && (
          <span className={styles.cutNote}>
            <svg width="14" height="7" viewBox="0 0 14 7" aria-hidden="true">
              <path d="M0 5 L3 1.5 L6 5 L9 1.5 L12 5" className={styles.cutLine} />
            </svg>
            세로축이 0에서 시작하지 않음
          </span>
        )}
      </div>

      <svg className={styles.svg} viewBox={`0 0 ${W} ${H}`} role="img" aria-label="기간별 MAU와 질의 건수 추이">
        {ticks.map((t) => {
          const ratio = t / 4;
          const y = PAD_T + PLOT_H * (1 - ratio);
          return (
            <g key={`tick-${t}`}>
              <line className={styles.gridLine} x1={PAD_L} y1={y} x2={PAD_L + PLOT_W} y2={y} />
              <text className={styles.axisText} x={PAD_L - 8} y={y + 3} textAnchor="end">
                {Math.round(mauDom.lo + (mauDom.hi - mauDom.lo) * ratio)}
              </text>
              <text className={styles.axisText} x={PAD_L + PLOT_W + 8} y={y + 3} textAnchor="start">
                {comma(Math.round(qDom.lo + (qDom.hi - qDom.lo) * ratio))}
              </text>
            </g>
          );
        })}

        <text className={styles.axisTitle} x={PAD_L - 8} y={PAD_T - 4} textAnchor="end" fill="#007CFF">MAU</text>
        <text className={styles.axisTitle} x={PAD_L + PLOT_W + 8} y={PAD_T - 4} textAnchor="start" fill="#003E80">질의</text>

        {isCut && <path className={styles.cutLine} d={zig} />}

        {points.map((p, i) =>
          has(p) ? (
            <rect
              key={`bar-${p.month}`}
              className={`${styles.bar} ${p.month === selectedMonth ? styles.barSelected : ''}`}
              x={cx(i) - barW / 2}
              y={yIn(p.mau, mauDom)}
              width={barW}
              height={Math.max(BASE_Y - yIn(p.mau, mauDom), 1)}
              rx={2}
            />
          ) : null
        )}

        {live.length > 1 && <polyline className={styles.qline} points={linePoints} />}

        {points.map((p, i) =>
          has(p) ? (
            <circle key={`dot-${p.month}`} className={styles.qdot} cx={cx(i)} cy={yIn(p.queries, qDom)} r={3.5} />
          ) : null
        )}

        {points.map((p, i) => (
          <text
            key={`label-${p.month}`}
            className={styles.axisText}
            x={cx(i)}
            y={BASE_Y + 18}
            textAnchor="middle"
            fill={has(p) ? '#636B74' : '#C4C4C4'}
          >
            {monthLabel(p.month, i)}
          </text>
        ))}

        {points.map((p, i) =>
          has(p) ? (
            <rect
              key={`hit-${p.month}`}
              className={styles.bandHit}
              x={PAD_L + band * i}
              y={PAD_T}
              width={band}
              height={PLOT_H}
              tabIndex={0}
              role="button"
              aria-label={`${p.month}, MAU ${p.mau}명, 질의 ${comma(p.queries)}건`}
              onClick={() => onSelectMonth(p.month)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onSelectMonth(p.month);
                }
              }}
              onMouseEnter={() => setHovered(i)}
              onFocus={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              onBlur={() => setHovered(null)}
            />
          ) : null
        )}
      </svg>

      {tipPoint && tipIndex !== null && (
        <div
          className={styles.tip}
          style={{ left: `${(cx(tipIndex) / W) * 100}%`, top: '42%' }}
          role="status"
        >
          <p className={styles.tipMonth}>{tipPoint.month.replace('-', '년 ')}월</p>
          <div className={styles.tipRow}>
            <span>MAU</span>
            <b>
              {tipPoint.mau}명
              <TrendMark value={delta(tipPoint.mau, points[tipIndex - 1]?.mau)} />
            </b>
          </div>
          <div className={styles.tipRow}>
            <span>질의</span>
            <b>
              {comma(tipPoint.queries)}건
              <TrendMark value={delta(tipPoint.queries, points[tipIndex - 1]?.queries)} />
            </b>
          </div>
        </div>
      )}

      <p className={styles.hint}>막대를 클릭하면 아래 질의 내역이 그 달로 바뀝니다.</p>
    </Box>
  );
}

function TrendMark({ value }: { value: number | null }) {
  if (value === null) return null;

  const cls = value > 0 ? styles.up : value < 0 ? styles.down : styles.flat;
  const sign = value > 0 ? '▲' : value < 0 ? '▼' : '–';

  return <span className={cls}> {sign}{Math.abs(value)}%</span>;
}
